import { useEffect } from 'react';
import { Col } from 'reactstrap';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowRight,
    faDatabase,
    faLayerGroup,
} from '@fortawesome/free-solid-svg-icons';
import { actions } from '../../../../../context';
import hasPermission from '../../../../Shared/hasPermission';

// Icon per shard type. Anything the API sends that isn't listed falls back to the staging
// icon, so surfacing a new shard type server-side degrades gracefully instead of rendering
// a blank row.
const SHARD_ICONS = {
    Staging: faDatabase,
    Intermediate: faLayerGroup,
};

// The shard types the API is expected to return, used only to draw the loading state — real
// rows always come from the response. Keeping the labels here means the block holds its
// height and tells the user what is arriving, rather than jumping when values land.
const EXPECTED_SHARDS = [
    { shardType: 'Staging', label: 'Staging' },
    { shardType: 'Intermediate', label: 'Intermediate' },
];

const iconFor = (shardType) => SHARD_ICONS[shardType] ?? faDatabase;

// A failed target-shard lookup is reported as unknown rather than as "not migrating", but it is a
// rare transport failure and does not warrant its own visible row. Surface it in the row's tooltip
// so whoever is debugging can tell "no migration" from "could not tell".
const titleFor = (shard) =>
    shard.isMigrationStatusUnknown
        ? `${shard.label} Shard — migration status unavailable`
        : `${shard.label} Shard`;

const ShardIcon = ({ shardType }) => (
    <>
        <FontAwesomeIcon
            className="text-light op-6"
            icon={iconFor(shardType)}
            size="lg"
        />
        &emsp;
    </>
);

const ShardRow = ({ children, shardType, title }) => (
    <Col md="12" className="d-flex px-3 py-2" title={title}>
        <ShardIcon shardType={shardType} />
        {children}
    </Col>
);

const PracticeShards = () => {
    const dispatch = useDispatch();

    const practiceId = useSelector((state) => state.practice.practice?.id);

    const { status, shards, loadedPracticeId } = useSelector(
        (state) => ({
            status: state.practice.practiceShards.status,
            shards: state.practice.practiceShards.shards,
            loadedPracticeId: state.practice.practiceShards.practiceId,
        }),
        shallowEqual
    );

    const permissions = useSelector(
        (state) => state.auth.permissions,
        shallowEqual
    );
    const canViewShardInformation = hasPermission(
        `ViewShardInformation`,
        permissions
    );

    useEffect(() => {
        if (!canViewShardInformation || !practiceId) return;
        dispatch(actions.practice.getPracticeShards(practiceId));
    }, [canViewShardInformation, practiceId, dispatch]);

    // No permission means no block and no request at all — not an empty placeholder.
    if (!canViewShardInformation) return null;

    // Treat a response belonging to a previously selected practice as still loading, so
    // switching practice tabs never shows the previous practice's state. Checked before
    // 'unavailable' as well as before the loaded rows: the reset to 'loading' happens in the
    // effect above, i.e. after the render that first sees the new practiceId, so without this
    // ordering a failed lookup on the previous practice would flash onto the new one.
    const isLoading =
        status === 'idle' ||
        status === 'loading' ||
        loadedPracticeId !== practiceId;

    // A 403 means the API disagrees with what the SPA believes the user can see — a permission
    // revoked mid-session, or locally the development override naming one the account does not
    // hold. That is not a shard-service outage, so render exactly what no permission renders:
    // nothing.
    if (!isLoading && status === 'forbidden') return null;

    if (!isLoading && status === 'unavailable') {
        return (
            <ShardRow shardType="Staging" title="Shard Information">
                <span className="op-6">Shard information unavailable</span>
            </ShardRow>
        );
    }

    if (isLoading) {
        return (
            <>
                {EXPECTED_SHARDS.map((shard) => (
                    <ShardRow
                        key={shard.shardType}
                        shardType={shard.shardType}
                        title={`${shard.label} Shard`}>
                        <span>
                            {shard.label}:{' '}
                            <span className="op-5">&mdash;</span>
                        </span>
                    </ShardRow>
                ))}
            </>
        );
    }

    // A practice with no instance has no shards to report. Render nothing rather than
    // claiming its shards are unassigned.
    if (!shards?.length) return null;

    return (
        <>
            {shards.map((shard) => (
                <ShardRow
                    key={shard.shardType}
                    shardType={shard.shardType}
                    title={titleFor(shard)}>
                    {shard.isAssigned ? (
                        <span>
                            {shard.label}: {shard.name}
                            {shard.region && (
                                <span className="op-6">
                                    &nbsp;&middot; {shard.region}
                                </span>
                            )}
                            {shard.isMigrationPending && (
                                <span className="d-block pl-1 sc-shard-migrating">
                                    <FontAwesomeIcon
                                        icon={faArrowRight}
                                        size="sm"
                                    />
                                    &nbsp; Migrating to {shard.targetName}
                                </span>
                            )}
                        </span>
                    ) : (
                        <span className="op-6">{shard.label}: Not assigned</span>
                    )}
                </ShardRow>
            ))}
        </>
    );
};

export default PracticeShards;
